/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputSwitchModule } from 'primeng/inputswitch';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { filter, Subscription } from 'rxjs';
import { TwoDigitNumberOnlyDirective } from '../../../core/direactives/uptotwoNumber.directive';
import { TranslatePipe } from '../../../core/pipe/translate.pipe';
import {
  preference,
  PreferencePayload,
  PreferencePayloadItem,
} from '../../../models/preference.model';
import { I18nService } from '../../../services/i18n.service';
import { preferenceService } from '../../../services/preference.service';
import { ToasterService } from '../../../services/toaster.service';

@Component({
  selector: 'logibids-prefrences',
  standalone: true,
  imports: [
    CommonModule,
    InputSwitchModule,
    ButtonModule,
    DropdownModule,
    ReactiveFormsModule,
    OverlayPanelModule,
    TranslatePipe,
    FormsModule,
    TwoDigitNumberOnlyDirective,
  ],
  templateUrl: './prefrences.component.html',
  styleUrls: ['./prefrences.component.scss'],
})
export class PrefrencesComponent implements OnInit, OnDestroy {
  private _prefrences: preference[] = [];
  @Input() set prefrences(value: preference[]) {
    this._prefrences = value;
    if (value && value.length) {
      this.initializeComponentState();
    }
  }
  get prefrences(): preference[] {
    return this._prefrences;
  }
  @Input() prefrencesValue: boolean = false;
  @Output() hidePrefernces = new EventEmitter<boolean>();

  form: FormGroup;
  save_preferences: boolean = false;

  // index of currently opened toggle-drop overlay
  currentSpecIndex: number | null = null;

  private subs: Subscription[] = [];
  formDisabled: boolean = false;
  // monkey-patch originals
  private origSetItem: ((k: string, v: string) => void) | null = null;
  private origRemoveItem: ((k: string) => void) | null = null;

  // flag whether handlers are currently installed (only true on matching route)
  private active = false;

  selectedIds: Array<number> = [];
  preferencePayload: any = null;

  constructor(
    private fb: FormBuilder,
    public _preferenceService: preferenceService,
    private router: Router,
    private toaster: ToasterService,
    private translate: I18nService,
  ) {
    this.form = this.fb.group({
      preferences: this.fb.array([]), // FormArray<FormGroup>
    });
  }

  ngOnInit(): void {
    // 6. Usual route logic
    this.checkAndToggleActive(this.router.url);
    const navSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: any) => this.checkAndToggleActive(e.urlAfterRedirects ?? e.url));
    this.subs.push(navSub);

    this.initializeComponentState();
  }

  private initializeComponentState(): void {
    if (!this._prefrences || !this._prefrences.length) return;

    // Reset form if it already has items (to handle re-initialization)
    this.preferencesArray.clear();

    // filter & sort incoming preferences (same as before)
    const raw = localStorage.getItem('preferencePayload');
    let payloadPrefs: number[] = [];

    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.preferences)) {
          payloadPrefs = parsed.preferences.map((p: any) => Number(p.preference_id));
          this.preferencePayload = parsed;
        }
      } catch {
        // ignore JSON error
      }
    }

    // 2. Filter & sort incoming preferences if payload present
    this._prefrences = (this._prefrences || [])
      .filter(val => val.is_visible_to_user === true)
      .sort((a, b) => (a.sorting ?? 0) - (b.sorting ?? 0));

    if (payloadPrefs.length && this.shouldLockToStoredPreferences()) {
      this._prefrences = this._prefrences.filter(pref => payloadPrefs.includes(Number(pref.id)));
    }

    // 3. Build form with filtered preferences
    const arr = this.preferencesArray;
    this._prefrences.forEach(p => {
      const group = this.createPrefGroup(p);
      if (p.type === 'dropdown') {
        const val = p?.option_id ?? null;
        if (val !== null && val !== undefined) {
          group.get('option_id')!.setValue(val);
        }
      }
      if (p.type === 'toggle') {
        const initial = p?.bool_value ?? false;
        group.get('bool_value')!.setValue(initial);
      }
      if (p.type === 'toggle-drop') {
        const multiArr = group.get('multi_selection') as FormArray;
        const incomingMulti = p?.options ?? p.options ?? null;
        if (Array.isArray(incomingMulti)) {
          incomingMulti.forEach((m: any) => {
            m['value'] = '';
            multiArr.push(this.createMultiSelectionGroup(m.id, m.value));
          });
        }
      }
      arr.push(group);
    });

    // 4. Apply payload if present (pre-fill the values)
    if (this.preferencePayload) {
      this.applyStoredPayload(this.preferencePayload);
      if (this.shouldLockToStoredPreferences()) {
        this.formDisabled = true;
        this.form.disable();
      }
    }

    this.patchValues();
  }

  patchValues(): void {
    // iterate over preferences with index (no need to re-find each item)
    this.save_preferences = this.prefrences.some(p => !!p.saved);
    this.prefrences.forEach((pref: any, prefIndex: number) => {
      const saved = pref?.saved;
      if (!saved) return; // nothing to patch for this pref

      const group = this.preferencesArray.at(prefIndex) as FormGroup;
      if (!group) return;

      const formPrefId = Number(group.get('preference_id')!.value);
      const savedPrefId = Number(saved.preference_id ?? saved.preferenceId ?? saved.preference_id);

      // sanity: ensure saved belongs to this pref (defensive)
      if (savedPrefId && formPrefId !== savedPrefId) {
        // if ids mismatch, try to find correct index instead
        const idx = this.prefrences.findIndex((p: any) => Number(p.id) === savedPrefId);
        if (idx !== -1) {
          // reassign group to the correct index
          const altGroup = this.preferencesArray.at(idx) as FormGroup;
          if (altGroup) {
            // patch into that group instead
            this.patchSinglePref(altGroup, this.prefrences[idx], saved);
          }
        }
        return;
      }

      // normal path: patch into current group
      this.patchSinglePref(group, pref, saved);
    });

    // update validations / UI
    this.preferencesArray.updateValueAndValidity();
    this.form.updateValueAndValidity();
  }

  /** helper to patch a single pref form group using saved object */
  private patchSinglePref(group: FormGroup, pref: any, saved: any) {
    const type = group.get('type')!.value;

    // utility to get an option id from common saved property names
    const getOptionId = (obj: any) =>
      obj?.option_id ?? obj?.optionId ?? obj?.selected_option_id ?? obj?.option?.id ?? null;

    if (type === 'dropdown') {
      const optionId = getOptionId(saved) ?? getOptionId(pref);
      if (optionId !== null && optionId !== undefined && optionId !== '') {
        group.get('option_id')!.setValue(Number(optionId));
      }
      return;
    }

    if (type === 'toggle') {
      const boolVal =
        saved?.bool_value ??
        saved?.value ??
        saved?.selected ??
        saved?.checked ??
        pref?.bool_value ??
        false;
      group.get('bool_value')!.setValue(!!boolVal);
      return;
    }

    if (type === 'toggle-drop') {
      // ensure multi_selection array exists
      const multiArr = group.get('multi_selection') as FormArray;

      // If saved provides an explicit array of selections
      if (Array.isArray(saved?.multi_selection) && saved.multi_selection.length > 0) {
        group.get('bool_value')!.setValue(true);
        // Clear existing to match saved payload
        multiArr.clear();
        saved.multi_selection.forEach((m: any) => {
          const optId = getOptionId(m) ?? m?.option_id;
          const optVal = m?.option_value ?? m?.value ?? m?.custom_value ?? '';
          if (optId !== undefined && optId !== null) {
            multiArr.push(this.createMultiSelectionGroup(Number(optId), optVal ?? ''));
          }
        });
        return;
      }

      // If saved includes single option_id + option_value / custom_value
      const singleOptId = getOptionId(saved);
      const singleOptVal = saved?.option_value ?? saved?.custom_value ?? saved?.value ?? null;
      if (singleOptId !== null && singleOptId !== undefined) {
        group.get('bool_value')!.setValue(true);
        // Clear then seed with pref options if you want consistent ordering, then set saved value
        multiArr.clear();

        // Try to seed from pref.options if available so overlay inputs match order
        const prefOptions = Array.isArray(pref?.options) ? pref.options : [];
        if (prefOptions.length) {
          prefOptions.forEach((o: any) => multiArr.push(this.createMultiSelectionGroup(o.id, '')));
          // set the saved value on matching control; if not found, push new control
          const found = multiArr.controls.find(
            c => Number(c.get('option_id')!.value) === Number(singleOptId),
          );
          if (found) {
            found.get('option_value')!.setValue(singleOptVal ?? '');
          } else {
            multiArr.push(this.createMultiSelectionGroup(Number(singleOptId), singleOptVal ?? ''));
          }
        } else {
          // no pref.options available — push the saved control(s)
          multiArr.push(this.createMultiSelectionGroup(Number(singleOptId), singleOptVal ?? ''));
        }
        return;
      }

      // If nothing meaningful in saved: set toggle off and clear values
      group.get('bool_value')!.setValue(false);
      multiArr.clear();
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.prefrences = [];
    this.disableHandlersIfActive();
  }

  private checkAndToggleActive(url: string) {
    const lowerUrl = url.toLowerCase();
    const shouldBeActive =
      lowerUrl.startsWith('/schedule-rent-transport') ||
      lowerUrl.startsWith('/transport/cabs') ||
      lowerUrl.startsWith('/transport/postenquiry');

    if (shouldBeActive) {
      if (!this.active) {
        this.enableHandlersForPreferencePayload();
      } else {
        // Already active, but might be a new matching route, re-apply payload
        this.loadFromLocalStorage();
      }
    } else if (this.active) {
      this.disableHandlersIfActive();
    }
  }

  private enableHandlersForPreferencePayload() {
    this.active = true;
    // load stored payload immediately
    this.loadFromLocalStorage();

    // listen for cross-tab changes
    window.addEventListener('storage', this.onStorageEvent);

    // monkey-patch localStorage methods to dispatch same-tab custom event
    this.installLocalStoragePatch();

    // listen for same-tab custom event
    window.addEventListener('preferencePayload:changed', this.onCustomPrefEvent as EventListener);
  }

  private disableHandlersIfActive() {
    if (!this.active) return;
    this.active = false;
    // remove listeners
    window.removeEventListener('storage', this.onStorageEvent);
    window.removeEventListener(
      'preferencePayload:changed',
      this.onCustomPrefEvent as EventListener,
    );
    // restore patched methods
    this.uninstallLocalStoragePatch();
  }

  private createPrefGroup(pref: preference): FormGroup {
    return this.fb.group({
      preference_id: [pref.id],
      type: [pref.type],
      option_id: [pref.type === 'dropdown' ? null : null],
      bool_value: [pref.type === 'toggle' || pref.type === 'toggle-drop' ? false : null],
      multi_selection: this.fb.array([]),
    });
  }

  private createMultiSelectionGroup(option_id: number, option_value: string | null) {
    return this.fb.group({
      option_id: [Number(option_id)],
      option_value: [option_value ?? ''],
    });
  }

  get preferencesArray(): FormArray {
    return this.form.get('preferences') as FormArray;
  }

  getMultiSelection(prefIndex: number): FormArray {
    return this.preferencesArray.at(prefIndex).get('multi_selection') as FormArray;
  }

  /**
   * When toggling a toggle-drop:
   * - If turning on: seed multi_selection with an entry per option (option_value empty).
   *   This binds each overlay input to a real FormControl so typed values are captured.
   * - Do NOT automatically send bool_value on its own.
   */
  onSpecToggle(event: any, index: number, op: OverlayPanel) {
    const checked = event && typeof event.checked === 'boolean' ? event.checked : !!event;

    const ctrl = this.preferencesArray.at(index).get('bool_value');
    if (ctrl && ctrl.value !== checked) {
      ctrl.setValue(checked, { emitEvent: false });
    }

    // ✅ Always clear all option value controls before proceeding
    const multiArr = this.getMultiSelection(index);
    if (multiArr && multiArr.length > 0) {
      multiArr.clear(); // remove all FormGroups inside FormArray
    }

    if (checked) {
      // ✅ Recreate option controls fresh for this index
      if (this.prefrences[index]?.options?.length) {
        this.prefrences[index].options.forEach((opt: any) => {
          multiArr.push(this.createMultiSelectionGroup(opt.id, ''));
        });
      }

      this.currentSpecIndex = index;
      const triggerEvent = event && event.originalEvent ? event.originalEvent : event;
      op.show(triggerEvent);
    } else {
      // ✅ Reset currentSpecIndex and close overlay
      this.currentSpecIndex = null;
      op.hide();
    }
  }

  // returns the FormControl for option_value for a given pref/option
  getOptionValueControl(prefIndex: number, optionId: number): FormControl {
    const arr = this.getMultiSelection(prefIndex);
    const grp = arr.controls.find(c => Number(c.value.option_id) === Number(optionId));
    if (grp) {
      // return the real control so two-way binding works
      return (grp.get('option_value') as FormControl) ?? new FormControl('');
    }
    // If not found (shouldn't happen because we seed on toggle ON), return disconnected control
    return new FormControl('');
  }

  onSpecSave(op: OverlayPanel) {
    if (this.currentSpecIndex === null) {
      return;
    }

    const multiArr = this.getMultiSelection(this.currentSpecIndex);
    // Check if all fields are empty
    const allEmpty = multiArr.controls.every(ctrl => {
      const val = ctrl.get('option_value')?.value;
      return val === null || val === undefined || val.toString().trim() === '';
    });

    if (allEmpty) {
      this.toaster.showError(this.translate.translate('Pleasefill'));
      return;
    }

    // Proceed if at least one field is filled
    this.currentSpecIndex = null;
    op.hide();
    this.toaster.showSuccess(this.translate.translate('Savedsuccessfully'));
  }

  hidePreferncesNext(): void {
    this.hidePrefernces.next(false);
  }

  /**
   * buildPayload: Only include toggle-drop preferences when
   * at least one multi_selection item has a non-empty option_value (trimmed).
   * Do NOT include bool_value fallback for toggle-drop.
   */
  buildPayload(): PreferencePayload {
    const raw = this.preferencesArray.value as any[];

    const mapped: PreferencePayloadItem[] = raw.map((item: any) => {
      const base: any = { preference_id: Number(item.preference_id) };

      if (item.type === 'toggle-drop') {
        // all items from the form array (may be seeded with empty values)
        const msAll = (item.multi_selection || [])
          .filter((m: any) => m.option_id !== undefined && m.option_id !== null)
          .map((m: any) => ({
            option_id: Number(m.option_id),
            option_value: (m.option_value ?? '').toString(),
          }));

        // keep only entries where option_value has content
        const msFiltered = msAll.filter(
          (m: any) => m.option_value !== null && m.option_value.toString().trim() !== '',
        );

        // Return multi_selection only when there's at least one filled value.
        if (msFiltered.length > 0) {
          return { ...base, multi_selection: msFiltered };
        }

        // otherwise return base (will be filtered out)
        return base;
      }

      // dropdown
      if (item.type === 'dropdown') {
        if (item.option_id !== null && item.option_id !== undefined && item.option_id !== '') {
          return { ...base, option_id: Number(item.option_id) };
        }
        return base;
      }

      // toggle
      if (item.type === 'toggle') {
        if (item.bool_value !== null && item.bool_value !== undefined) {
          return { ...base, bool_value: !!item.bool_value };
        }
        return base;
      }

      return base;
    });

    const filtered = mapped.filter(m => {
      return (
        (m.multi_selection && m.multi_selection.length > 0) ||
        m.option_id !== undefined ||
        (m.bool_value !== undefined && m.bool_value !== false)
      );
    });

    return { preferences: filtered };
  }

  onSubmit(): void {
    const payload = this.buildPayload();
    payload['save_preferences'] = this.save_preferences;
    if (payload.preferences && payload.preferences.length > 0) {
      this._preferenceService.updatedPreference(payload);
      this.hidePreferncesNext();
      localStorage['preferencePayload'] = JSON.stringify(payload);
    } else if (payload.preferences && payload.preferences.length === 0) {
      const hadPrefs =
        !!localStorage.getItem('preferencePayload') ||
        !!this._preferenceService.preferencePayload()?.preferences?.length;
      if (hadPrefs) {
        this.clearStoredPreferences();
      }
      this.hidePreferncesNext();
    }
  }

  /** Where-to-go stays editable; other screens keep the locked stored-pref view. */
  private isWhereToGoRoute(): boolean {
    return this.router.url.toLowerCase().includes('/transport/wheretogo');
  }

  private shouldLockToStoredPreferences(): boolean {
    return !this.isWhereToGoRoute();
  }

  get canEditPreferences(): boolean {
    return this.preferencePayload === null || this.isWhereToGoRoute();
  }

  private clearStoredPreferences(): void {
    this.preferencePayload = null;
    this.formDisabled = false;
    this.form.enable();
    localStorage.removeItem('preferencePayload');
    this._preferenceService.updatedPreference({ preferences: [] });
  }

  //LocalStorage
  // ----- load / apply stored payload (only invoked while active) -----
  private loadFromLocalStorage(): void {
    const raw = localStorage.getItem('preferencePayload');
    this.preferencePayload = JSON.parse(localStorage.getItem('preferencePayload'));
    this.handleStoredPayloadString(raw);
  }

  private handleStoredPayloadString(raw: string | null) {
    if (!raw) return;

    let parsed: any = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      console.warn('Invalid preferencePayload in localStorage', raw);
      return;
    }

    if (!parsed || !Array.isArray(parsed.preferences)) return;

    this.applyStoredPayload(parsed);
  }

  applyStoredPayload(payload: PreferencePayload) {
    payload.preferences.forEach((pItem: any) => {
      const prefId = Number(pItem.preference_id);
      const index = this.prefrences.findIndex(pr => Number(pr.id) === prefId);
      if (index === -1) return;

      const group = this.preferencesArray.at(index);
      const type = group.get('type')!.value;

      if (type === 'dropdown') {
        if (pItem.option_id !== undefined && pItem.option_id !== null) {
          group.get('option_id')!.setValue(Number(pItem.option_id));
        }
      } else if (type === 'toggle') {
        if (pItem.bool_value !== undefined && pItem.bool_value !== null) {
          group.get('bool_value')!.setValue(!!pItem.bool_value);
        }
      } else if (type === 'toggle-drop') {
        if (Array.isArray(pItem.multi_selection) && pItem.multi_selection.length > 0) {
          group.get('bool_value')!.setValue(true);
          const multiArr = group.get('multi_selection') as FormArray;
          multiArr.clear();
          pItem.multi_selection.forEach((m: any) => {
            if (m.option_id !== undefined && m.option_id !== null) {
              multiArr.push(
                this.createMultiSelectionGroup(Number(m.option_id), m.option_value ?? ''),
              );
            }
          });
        } else {
          group.get('bool_value')!.setValue(false);
          (group.get('multi_selection') as FormArray).clear();
        }
      }
    });

    if (this.shouldLockToStoredPreferences()) {
      this.formDisabled = true;
      this.form.disable();

      const raw = localStorage.getItem('preferencePayload');
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && Array.isArray(parsed.preferences)) {
        this.selectedIds = parsed.preferences.map((p: any) => Number(p.preference_id));
        this.keepOnlyPreferencesByIds(this.selectedIds);
      }
    }
  }

  enableForm() {
    this.formDisabled = false;
    this.form.enable();
  }

  isDirectClick(event: Event): boolean {
    const target = event.target as HTMLElement;
    return !!(
      target.closest('.p-dropdown') ||
      target.closest('p-inputswitch') ||
      target.closest('.p-inputswitch') ||
      (target.closest('.p-element') &&
        target.closest('.p-element')?.tagName.toLowerCase() === 'p-inputswitch')
    );
  }

  handleSaveForFutureClick(event: Event) {
    if (this.formDisabled || this.isDirectClick(event)) {
      return;
    }
    this.save_preferences = !this.save_preferences;
  }

  handleRowClick(event: Event, index: number, op: OverlayPanel, listItem: HTMLElement) {
    if (this.formDisabled) return;

    // Prevent triggering twice if the user actually clicked on the switch or dropdown directly
    if (this.isDirectClick(event)) {
      return;
    }

    const pref = this.prefrences[index];
    const group = this.preferencesArray.at(index);

    if (pref.type === 'toggle') {
      const ctrl = group.get('bool_value');
      ctrl?.setValue(!ctrl.value);
    } else if (pref.type === 'toggle-drop') {
      const ctrl = group.get('bool_value');
      const newVal = !ctrl?.value;
      ctrl?.setValue(newVal);
      // Manually trigger the toggle handler since setValue doesn't trigger UI components' (onChange)
      this.onSpecToggle({ checked: newVal, originalEvent: event }, index, op);
    } else if (pref.type === 'dropdown') {
      // Find the dropdown's trigger/container and simulate a click
      const dropDownTrigger = listItem.querySelector('.p-dropdown');
      if (dropDownTrigger) {
        (dropDownTrigger as HTMLElement).click();
      }
    }
  }

  // ----- events & monkey-patch handling (active only on matching route) -----

  keepOnlyPreferencesByIds(ids: number[]): void {
    const prefs = this.form.get('preferences') as FormArray;
    if (!prefs) return;

    const idSet = new Set(ids); // convert array to set for faster lookup

    // iterate backwards and remove non-matching from both FormArray and Metadata Array
    for (let i = prefs.length - 1; i >= 0; i--) {
      const group = prefs.at(i) as FormGroup;
      const prefId = group.get('preference_id')?.value;
      if (!idSet.has(Number(prefId))) {
        prefs.removeAt(i);
        // Sync the metadata array as well
        if (this.prefrences[i]) {
          this.prefrences.splice(i, 1);
        }
      }
    }

    prefs.updateValueAndValidity();
    this.form.updateValueAndValidity();
  }

  /** storage event from other tabs */
  private onStorageEvent = (ev: StorageEvent) => {
    if (!this.active) return;
    if (ev.key === 'preferencePayload') {
      this.handleStoredPayloadString(ev.newValue);
    }
  };

  /** handler for our custom same-tab event */
  private onCustomPrefEvent = (ev: Event) => {
    if (!this.active) return;
    const ce = ev as CustomEvent<{ key: string; newValue: string | null }>;
    if (!ce?.detail) return;
    const { key, newValue } = ce.detail;
    if (key === 'preferencePayload') {
      this.handleStoredPayloadString(newValue);
    }
  };

  /** Replace localStorage.setItem/removeItem with wrappers that dispatch a custom event */
  private installLocalStoragePatch() {
    if ((this.origSetItem && this.origRemoveItem) || typeof window === 'undefined') return;

    this.origSetItem = localStorage.setItem;
    this.origRemoveItem = localStorage.removeItem;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    // patch setItem
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (localStorage as any).setItem = function (key: string, value: string) {
      self.origSetItem!.call(localStorage, key, value);
      try {
        const ev = new CustomEvent('preferencePayload:changed', {
          detail: { key, newValue: value },
        });
        window.dispatchEvent(ev);
      } catch {
        // ignore
      }
    };

    // patch removeItem
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (localStorage as any).removeItem = function (key: string) {
      self.origRemoveItem!.call(localStorage, key);
      try {
        const ev = new CustomEvent('preferencePayload:changed', {
          detail: { key, newValue: null },
        });
        window.dispatchEvent(ev);
      } catch {
        // ignore
      }
    };
  }

  /** restore original localStorage methods */
  private uninstallLocalStoragePatch() {
    try {
      if (this.origSetItem) {
        (localStorage as any).setItem = this.origSetItem;
        this.origSetItem = null;
      }
      if (this.origRemoveItem) {
        (localStorage as any).removeItem = this.origRemoveItem;
        this.origRemoveItem = null;
      }
    } catch {
      // ignore restore errors
    }
  }

  onOverlayHide() {
    if (this.currentSpecIndex !== null) {
      // Reset toggle bool_value to false (default)
      const group = this.preferencesArray.at(this.currentSpecIndex);
      if (group) {
        group.get('bool_value')?.setValue(false, { emitEvent: false });

        // Optional: clear multi_selection if you want to reset inputs on overlay dismiss
        const multiArr = group.get('multi_selection') as FormArray;
        if (multiArr) {
          multiArr.clear();
        }
      }
    }
  }
}
